import type { QueryClient } from "@tanstack/react-query";
import { logger } from "@/lib/logger";

/**
 * Caches du service worker qui contiennent des réponses propres à un compte.
 * Voir src/sw.ts : seul le cache REST Supabase porte des données utilisateur.
 * Les caches de coquille (`employee-shell-cache`, `navigation-cache`) et le
 * précache Workbox ne contiennent que le HTML/JS de l'app, identique pour
 * tout le monde : les purger ne protégerait rien et coûterait le démarrage
 * hors ligne.
 */
const ACCOUNT_SCOPED_SW_CACHES = ["supabase-api-cache"];

/**
 * Clés localStorage qui portent le contexte du compte — à distinguer des
 * préférences d'affichage (thème, consentement cookies, tour guidé, bannière
 * PWA) qui n'identifient personne et qu'on laisse en place.
 */
const ACCOUNT_SCOPED_STORAGE_KEYS = ["selectedStore"];

/**
 * Efface tout ce qui, à la déconnexion, survivrait au compte qui vient de partir.
 *
 * POURQUOI. `signOut()` révoque le jeton, rien de plus : il ne touche ni au
 * cache React Query, ni aux caches du service worker, ni au magasin retenu
 * dans localStorage. Or la déconnexion enchaîne sur un `navigate()` — une
 * navigation cliente, pas un rechargement — donc le contexte JS survit intact,
 * et le cache React Query vit encore `gcTime` (10 min, App.tsx).
 *
 * Sur un poste partagé (PC de la réserve, tablette du magasin), le compte
 * suivant héritait donc des données du précédent : les clés de queryKeys.ts
 * sont indexées par magasin ou par salarié, jamais par utilisateur connecté,
 * et un gérant partage le `store_id` de ses salariés. `planningSigning.week`
 * en est l'exemple concret — même clé `(storeId, weekStart)` peuplée par le
 * suivi RH du gérant (SuiviRhWeekMatrixSection.tsx) et lue par le tableau de
 * bord salarié (EmployeeDashboard.tsx), avec des lignes que la RLS n'aurait
 * jamais renvoyées au salarié.
 *
 * À appeler APRÈS `signOut()` et AVANT la navigation : si la navigation
 * échoue ou n'aboutit pas, la purge a tout de même eu lieu.
 *
 * Chaque étape est isolée : un `localStorage` indisponible (Safari en
 * navigation privée) ou un `caches` absent (contexte non sécurisé, jsdom) ne
 * doit pas empêcher les autres de s'exécuter.
 */
export async function clearAccountScopedCaches(queryClient: QueryClient): Promise<void> {
  try {
    queryClient.clear();
  } catch (e) {
    logger.error("Failed to clear React Query cache on sign-out", e);
  }

  for (const key of ACCOUNT_SCOPED_STORAGE_KEYS) {
    try {
      localStorage.removeItem(key);
    } catch (e) {
      logger.error(`Failed to remove localStorage key "${key}" on sign-out`, e);
    }
  }

  if (typeof caches === "undefined") return;

  await Promise.all(
    ACCOUNT_SCOPED_SW_CACHES.map(async (name) => {
      try {
        await caches.delete(name);
      } catch (e) {
        logger.error(`Failed to delete service worker cache "${name}" on sign-out`, e);
      }
    }),
  );
}
